import React from 'react'
import { Box, Card, Flex, Heading, Link, Text } from '@workwhile/ui'
import * as ROUTES from 'constants/routes'
import { useIndeedFlexCompaniesContextValue } from '../context/IndeedFlexCompaniesProvider'
import { EditCompanyButton } from './EditCompanyButton'

type IndeedFlexCompanyItem = ReturnType<
  typeof useIndeedFlexCompaniesContextValue
>['state']['data']['items'][number]

const DetailRow = ({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) => {
  return (
    <Flex gap={2} alignItems="baseline">
      <Text fontSize={1} color="lightText" style={{ minWidth: 128 }}>
        {label}
      </Text>
      <Box flex={1}>{children}</Box>
    </Flex>
  )
}

const SignedForm = ({
  fileHandleId,
  fileHandleName,
}: {
  fileHandleId: number | null
  fileHandleName: string | null
}) => {
  if (fileHandleId === null || fileHandleName === null) {
    return <Text color="lightText">None</Text>
  }

  const displayName = fileHandleName.split('/').pop() || fileHandleName
  return (
    <Text>
      {displayName} ({fileHandleId})
    </Text>
  )
}

const ShiftNotification = ({
  deliveryHour,
  title,
  message,
}: {
  deliveryHour: number | null
  title: string | null
  message: string | null
}) => {
  if (deliveryHour === null || message === null) {
    return <Text color="lightText">None</Text>
  }

  return (
    <Box>
      <Text>
        {title ?? '(no title)'}{' '}
        <Text as="span" fontSize={1} color="lightText">
          · {deliveryHour}h before shift
        </Text>
      </Text>
      <Text fontSize={1} color="lightText">
        {message}
      </Text>
    </Box>
  )
}

const IndeedFlexCompanyCard = ({
  company,
}: {
  company: IndeedFlexCompanyItem
}) => {
  return (
    <Card p={3}>
      <Flex
        justifyContent="space-between"
        alignItems="flex-start"
        gap={3}
        mb={3}
        flexWrap="wrap"
      >
        <Box>
          <Heading level={4} my={0}>
            {company.companyName}{' '}
            <Link
              to={ROUTES.COMPANY_DETAIL.replace(
                ':companyId',
                company.companyId.toString()
              )}
              target="_blank"
            >
              ({company.companyId})
            </Link>
          </Heading>
          <Text fontSize={1} color="lightText" mt={1}>
            {company.clientName} ({company.ifClientId})
          </Text>
        </Box>
        <EditCompanyButton company={company} />
      </Flex>

      <Flex flexDirection="column" gap={1} mb={3}>
        <DetailRow label="Create shifts automatically">
          <Text>{company.canCreateShifts ? 'Yes' : 'No'}</Text>
        </DetailRow>
        <DetailRow label="Signed form">
          <SignedForm
            fileHandleId={company.fileHandleId}
            fileHandleName={company.fileHandleName}
          />
        </DetailRow>
        <DetailRow label="Shift notification">
          <ShiftNotification
            deliveryHour={company.shiftNotificationDeliveryHour}
            title={company.shiftNotificationTitle}
            message={company.shiftNotificationMessage}
          />
        </DetailRow>
      </Flex>

      <Flex gap={4} flexWrap="wrap">
        <Box flex={1} style={{ minWidth: 260 }}>
          <Text fontWeight="bold" mb={2}>
            Locations ({company.locations.length})
          </Text>
          {company.locations.length === 0 ? (
            <Text color="lightText">No linked locations yet.</Text>
          ) : (
            <Flex flexDirection="column" gap={1}>
              {company.locations.map((location) => (
                <Text key={location.ifVenueId}>
                  {location.venueName}{' '}
                  <Text as="span" fontSize={1} color="lightText">
                    · IF venue ({location.ifVenueId}) · WW location (
                    {location.locationId})
                  </Text>
                </Text>
              ))}
            </Flex>
          )}
        </Box>

        <Box flex={1} style={{ minWidth: 260 }}>
          <Text fontWeight="bold" mb={2}>
            Positions ({company.positions.length})
          </Text>
          {company.positions.length === 0 ? (
            <Text color="lightText">No linked positions yet.</Text>
          ) : (
            <Flex flexDirection="column" gap={1}>
              {company.positions.map((position) => (
                <Text key={`${position.ifRoleId}-${position.positionId}`}>
                  {position.roleName}{' '}
                  <Text as="span" fontSize={1} color="lightText">
                    · IF role ({position.ifRoleId}) · WW position (
                    {position.positionId})
                  </Text>
                </Text>
              ))}
            </Flex>
          )}
        </Box>
      </Flex>
    </Card>
  )
}

export const IndeedFlexCompaniesCards = () => {
  const {
    state: { data },
  } = useIndeedFlexCompaniesContextValue()

  if (data.items.length === 0) {
    return (
      <Flex
        mb={2}
        color="lightText"
        height={258}
        alignItems="center"
        justifyContent="center"
      >
        No Indeed Flex companies yet. Add one to get started.
      </Flex>
    )
  }

  return (
    <Flex flexDirection="column" gap={3}>
      {data.items.map((company) => (
        <IndeedFlexCompanyCard key={company.id} company={company} />
      ))}
    </Flex>
  )
}
